/**
 * Green Flag — staff workspace styles
 *
 * Style objects shared by the admin and check-in chrome, for the looks that
 * are not a single token: the focus ring, the pill switch segment, the
 * track a switch of radio segments sits in, and the figures of a ledger.
 */

import type { SystemStyleObject } from "@chakra-ui/react";

/**
 * Keyboard focus in a staff workspace: a solid ring clear of the control. The
 * recipes' translucent ring is lost on the dark top bar; this one reads on the
 * bar and on white.
 */
export const ADMIN_FOCUS_RING: SystemStyleObject = {
  outline: "2px solid",
  outlineColor: "admin.focus",
  outlineOffset: "2px",
};

/**
 * Figures that line up down a column: every digit as wide as the next, so
 * amounts and counts in a ledger read as a column of numbers.
 */
export const TABULAR_NUMS: SystemStyleObject = {
  fontVariantNumeric: "tabular-nums",
};

/**
 * A number field with the browser's spin buttons hidden, for a field whose
 * own controls step it (a stepper) or whose units sit around it (a price
 * with "$" and "/ send"). Its figures are tabular, like the ledger it sits in.
 */
export const BARE_NUMBER_INPUT: SystemStyleObject = {
  ...TABULAR_NUMS,
  appearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

/** How a pill button in a staff workspace's filter row changes colour. */
export const ADMIN_PILL_TRANSITION: SystemStyleObject = {
  transitionProperty: "background-color, color, border-color",
  transitionDuration: "normal",
  transitionTimingFunction: "standard",
};

/**
 * One segment of a staff workspace's pill switch, such as ModeSwitch's
 * "App · Admin" and the events page's List / Calendar. The chosen segment is
 * the mint pill. It keys off `aria-current="page"` for a segment that is a
 * link and `aria-checked` for one that is a radio, so no segment branches on
 * whether it is chosen.
 */
export const adminSegmentStyles = (dark: boolean): SystemStyleObject => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "1.5",
  borderRadius: "pill",
  textStyle: "buttonLabelInactive",
  whiteSpace: "nowrap",
  color: dark ? "admin.chrome.fgMuted" : "fg.muted",
  transitionProperty: "background-color, color",
  transitionDuration: "normal",
  transitionTimingFunction: "standard",
  _hover: { color: dark ? "admin.chrome.fg" : "fg.default" },
  _focusVisible: ADMIN_FOCUS_RING,
  "&[aria-current='page'], &[aria-checked='true']": {
    textStyle: "buttonLabel",
    bg: "bg.brandSoft",
    color: "fg.onTint",
  },
});

/**
 * The track of a staff workspace's switch whose segments are radios, such as
 * the events page's List / Calendar and a calendar's Month / Week.
 */
export const ADMIN_SEGMENT_TRACK: SystemStyleObject = {
  gap: "0.5",
  p: "3px",
  borderRadius: "pill",
  borderWidth: "1px",
  borderColor: "border.default",
  bg: "bg.canvas",
};

/** One radio segment in `ADMIN_SEGMENT_TRACK`, as tall as the track. */
export const ADMIN_RADIO_SEGMENT: SystemStyleObject = {
  ...adminSegmentStyles(false),
  h: "100%",
  border: "0",
  bg: "transparent",
  cursor: "pointer",
};
